import { Injectable } from "@angular/core";
import { FormatRuleOptions, RuleGeneratorService } from "./rule-generator.service";
import { ItemDataService } from "../item-data.service";
import { poe1, poe2 } from "../../../game";
import { filterHeader } from "../../filter-template";
import { DefenceType, AttackWeaponsCategory, IdentifiedModTier, CasterWeaponsCategory, GemWeaponsCategory, QuiversCategory, JewelleryCategory, ModItemCategoryType, ITEM_CATEGORIES_POE1_ONLY, ITEM_CATEGORIES_POE2_ONLY, LifeArmourCategory, EnergyShieldArmourCategory, CustomModItemType, JewelleryType, YesNoAny, JewelsCategory, AbyssJewelsCategory, TimeLostJewelsCategory, Rarity } from "../../models/enums";
import { Filter, CustomRule, AdvancedConditions, IdentifiedItemConfig, IdentifiedItemConfigType, ItemTier, DisplayOptions } from "../../models/filter-models";
import { Comparator } from "../../models/enums";
import { WeaponType, ArmourType, BaseTypeTier } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { ModTierService } from "../tiering/mod-tier.service";
import { GenerationHelperService } from "./generation-helper.service";

export const RARE_LEVEL_DIFF = 20;
export const NORMAL_WITH_MATCHING_SOCKET_GROUP_LEVEL_DIFF = 10;
export const NORMAL_LEVEL_DIFF = 5;

@Injectable({
  providedIn: 'root'
})
export class IdentifiedItemGeneratorService {
  oneHandWeaponTypes: WeaponType[];

  static cache: { [key: string]: string } = {};

  constructor(private ruleFormatter: RuleGeneratorService, private itemDataService: ItemDataService,
    private modTierService: ModTierService, private helper: GenerationHelperService
  ) {
    this.oneHandWeaponTypes = this.itemDataService.getOneHandWeaponTypes();
  }

  private formatDefenceType(defenceTypes: DefenceType[]) {
    let canContainArmour = false, canContainEnergyShield = false, canContainEvasion = false;

    for (const defenceType of defenceTypes) {
      if (defenceType === DefenceType.Armour || defenceType === DefenceType.ArmourEnergyShield || defenceType === DefenceType.ArmourEvasion) {
        canContainArmour = true;
      }
      if (defenceType === DefenceType.EnergyShield || defenceType === DefenceType.EvasionEnergyShield || defenceType === DefenceType.ArmourEnergyShield) {
        canContainEnergyShield = true;
      }
      if (defenceType === DefenceType.Evasion || defenceType === DefenceType.ArmourEvasion || defenceType === DefenceType.EvasionEnergyShield) {
        canContainEvasion = true;
      }
    }

    let filter = '';

    if (!canContainArmour) filter += '\n  BaseArmour == 0';
    if (!canContainEnergyShield) filter += '\n  BaseEnergyShield == 0';
    if (!canContainEvasion) filter += '\n  BaseEvasion == 0';

    return filter;
  }

  buildItemClass(showChaosRecipeTarget: string[]): string | undefined {
    let itemClass = '';
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Helmets')) {
      itemClass += '"Helmets" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Gloves')) {
      itemClass += '"Gloves" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Boots')) {
      itemClass += '"Boots" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Body Armours')) {
      itemClass += '"Body Armours" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Weapons')) {
      itemClass += '"Bows" "Two Hand Axes" "Two Hand Maces" "Two Hand Swords" "Warstaves" "Staves" ';
    }
    if (showChaosRecipeTarget.includes('All') || showChaosRecipeTarget.includes('Jewellery')) {
      itemClass += '"Rings" "Amulets" "Belts" ';
    }

    return itemClass.trim();
  }

  public generateIdentifiedItemsRules(filter: Filter): string {
    const cacheKey = JSON.stringify(filter.cosmeticSettings)
      + JSON.stringify(filter.tierCosmetics)
      + JSON.stringify(filter.identifiedItemConfigs)
      + JSON.stringify(filter.showAllIdentifiedItems)
      + JSON.stringify(filter.showIdentifiedGear)
      + JSON.stringify(filter.showIdentifiedJewellery)
      + JSON.stringify(filter.showIdentifiedJewels)
      + JSON.stringify(filter.showIdentifiedCustomItems)
      + JSON.stringify(filter.showIdentifiedAttackWeaponCategories)
      + JSON.stringify(filter.showIdentifiedAttackWeaponTypes)
      + JSON.stringify(filter.showIdentifiedCasterWeaponCategories)
      + JSON.stringify(filter.showIdentifiedGemWeaponCategories)
      + JSON.stringify(filter.showIdentifiedQuiverCategories)
      + JSON.stringify(filter.showIdentifiedLifeArmourCategories)
      + JSON.stringify(filter.showIdentifiedLifeArmourTypesDefenceTypes)
      + JSON.stringify(filter.showIdentifiedEnergyShieldArmourCategories)
      + JSON.stringify(filter.showIdentifiedEnergyShieldArmourTypesDefenceTypes)
      + JSON.stringify(filter.showIdentifiedJewelleryCategories)
      + JSON.stringify(filter.showIdentifiedJewelCategories)
      + JSON.stringify(filter.showIdentifiedAbyssJewelCategories)
      + JSON.stringify(filter.showIdentifiedTimeLostJewelCategories)
      + JSON.stringify(filter.identifiedItemsStyleOverride)
      + JSON.stringify(filter.customGearTiers)
      + JSON.stringify(filter.customModTiers)
      + JSON.stringify(filter.customEquipmentTiers)
      + JSON.stringify(filter.customModItemCategories)
      + JSON.stringify(filter.equipmentCosmeticsSameAsTierCosmetics);

    if (IdentifiedItemGeneratorService.cache[cacheKey])
      return IdentifiedItemGeneratorService.cache[cacheKey];

    if (filter.showAllIdentifiedItems) {
      const allItems = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherAllIdentifiedItems, new CustomRule({
        advancedConditions: new AdvancedConditions({ identified: YesNoAny.Yes }),
      }), filter) + '\n\n';

      IdentifiedItemGeneratorService.cache[cacheKey] = allItems;
      return IdentifiedItemGeneratorService.cache[cacheKey];
    }

    const enabledEquipmentConfigs = filter.identifiedItemConfigs.filter(c => c.enabled && c.type === IdentifiedItemConfigType.Equipment);
    const enabledJewelConfigs = filter.identifiedItemConfigs.filter(c => c.enabled && c.type === IdentifiedItemConfigType.Jewels);

    let weaponRules = '';
    let casterWeaponRules = '';
    let gemWeaponRules = '';
    let quiverRules = '';
    let lifeArmourRules = '';
    let esArmourRules = '';
    let jewelleryRules = '';
    let jewelRules = '';
    let abyssJewelRules = '';
    let timeLostJewelRules = '';
    let customItemRules = '';

    if (filter.showIdentifiedGear) {
      weaponRules = filterHeader.replace('{header}', 'Identified Attack Weapons');
      const allAttackWeaponCategories = [AttackWeaponsCategory.MeleePhysical, AttackWeaponsCategory.MeleeElemental, AttackWeaponsCategory.RangedPhysical, AttackWeaponsCategory.RangedElemental];
      const selectedAttackWeaponCategories =
        filter.showIdentifiedAttackWeaponCategories.includes(AttackWeaponsCategory.All)
          ? allAttackWeaponCategories
          : filter.showIdentifiedAttackWeaponCategories.includes(AttackWeaponsCategory.None)
            ? []
            : filter.showIdentifiedAttackWeaponCategories;

      for (const category of selectedAttackWeaponCategories) {
        if (selectedAttackWeaponCategories.includes(category)) {
          let selectedWeaponClasses = filter.showIdentifiedAttackWeaponTypes.includes(WeaponType.All) ? this.itemDataService.getAttackWeaponTypes() : filter.showIdentifiedAttackWeaponTypes;

          if (category === AttackWeaponsCategory.RangedPhysical || category === AttackWeaponsCategory.RangedElemental) {
            selectedWeaponClasses = selectedWeaponClasses.filter(w => this.itemDataService.getRangedWeaponTypes().includes(w as WeaponType));
          }

          const filterItemClass = selectedWeaponClasses.map(w => `"${w}"`).join(' ');

          for (const config of enabledEquipmentConfigs) {
            const baseTypes = config.specificBaseTypes
              ? config.specificBaseTypes
              : (config.minGearTier
                ? this.helper.buildBaseTypesForTier(config.minGearTier, selectedWeaponClasses)
                : ['All']);

            const modLine = this.buildModLine(category, config, filter);
            weaponRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
              this.getEquipmentType(config, 'gear'),
              new CustomRule({
                filterItemClass,
                rarity: config.rarity,
                rarityComparator: config.rarityComparator,
                advancedConditions: new AdvancedConditions({
                  corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                  identified: YesNoAny.Yes,
                  minItemLevel: config.minILevel
                }),
                baseTypes,
              }),
            filter,
            this.buildIdentifiedItemName(config, category, 'Attack Weapons'),
            modLine
          ) + '\n\n';
          }
        }
      }

      casterWeaponRules = filterHeader.replace('{header}', 'Identified Caster Weapons');
      const allCasterWeaponCategories = [CasterWeaponsCategory.Chaos, CasterWeaponsCategory.Cold, CasterWeaponsCategory.Fire, CasterWeaponsCategory.Lightning, CasterWeaponsCategory.Minion, CasterWeaponsCategory.Physical];
      const selectedCasterWeaponCategories = filter.showIdentifiedCasterWeaponCategories.includes(CasterWeaponsCategory.All)
        ? allCasterWeaponCategories
        : filter.showIdentifiedCasterWeaponCategories.includes(CasterWeaponsCategory.None)
          ? []
          : filter.showIdentifiedCasterWeaponCategories;

      for (const category of selectedCasterWeaponCategories) {
        const weaponTypes = category === CasterWeaponsCategory.Minion
          ? [poe1 ? WeaponType.Wands : WeaponType.Sceptres]
          : poe1
            ? [WeaponType.RuneDaggers, WeaponType.Sceptres, WeaponType.Wands]
            : [WeaponType.Staves, WeaponType.Wands];
        const filterItemClass = weaponTypes.map(w => `"${w}"`).join(' ');

        for (const config of enabledEquipmentConfigs) {
          let baseTypes: string[];
          if (config.specificBaseTypes) {
            baseTypes = config.specificBaseTypes;
          } else if (poe1 && category === CasterWeaponsCategory.Minion && config.minGearTier) {
            baseTypes = config.minGearTier === BaseTypeTier.Elite || config.minGearTier === BaseTypeTier.Expert || config.minGearTier === BaseTypeTier.Master
              ? ['Convoking Wand', 'Convening Wand']
              : ['Convoking Wand', 'Convening Wand', 'Calling Wand'];
          } else if (config.minGearTier) {
            baseTypes = this.helper.buildBaseTypesForTier(config.minGearTier, weaponTypes);
          } else {
            baseTypes = ['All'];
          }

          const modLine = this.buildModLine(category, config, filter);
          casterWeaponRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
            this.getEquipmentType(config, 'gear'),
            new CustomRule({
              filterItemClass,
              rarity: config.rarity,
              rarityComparator: config.rarityComparator,
              advancedConditions: new AdvancedConditions({
                corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                identified: YesNoAny.Yes,
                minItemLevel: config.minILevel
              }),
              baseTypes,
            }),
            filter,
            this.buildIdentifiedItemName(config, category, 'Caster Weapons'),
            modLine
          ) + '\n\n';
        }
      }

      if (poe1) {
        gemWeaponRules = filterHeader.replace('{header}', 'Identified Gem Weapons');
        const allGemWeaponCategories = [GemWeaponsCategory.Bows, GemWeaponsCategory.Caster, GemWeaponsCategory.Staves];
        const selectedGemWeaponCategories = filter.showIdentifiedGemWeaponCategories.includes(GemWeaponsCategory.All)
          ? allGemWeaponCategories
            : filter.showIdentifiedGemWeaponCategories.includes(GemWeaponsCategory.None)
            ? []
            : filter.showIdentifiedGemWeaponCategories;

        for (const category of selectedGemWeaponCategories) {
          const weaponTypes = category === GemWeaponsCategory.Bows ? [WeaponType.Bows] : category === GemWeaponsCategory.Caster ? [WeaponType.RuneDaggers, WeaponType.Wands, WeaponType.Sceptres] : [WeaponType.Staves, WeaponType.Warstaves];
          const filterItemClass = weaponTypes.map(w => `"${w}"`).join(' ');

          for (const config of enabledEquipmentConfigs) {
            const baseTypes = config.specificBaseTypes
              ? config.specificBaseTypes
              : (config.minGearTier
                ? this.helper.buildBaseTypesForTier(config.minGearTier, weaponTypes)
                : ['All']);

            const modLine = this.buildModLine(category, config, filter);
            gemWeaponRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
              this.getEquipmentType(config, 'gear'),
              new CustomRule({
                filterItemClass,
                rarity: config.rarity,
                rarityComparator: config.rarityComparator,
                advancedConditions: new AdvancedConditions({
                  corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                  identified: YesNoAny.Yes,
                  minItemLevel: config.minILevel
                }),
                baseTypes,
              }),
            filter,
            this.buildIdentifiedItemName(config, category, 'Gem Weapons'),
            modLine
          ) + '\n\n';
          }
        }
      }

      quiverRules = filterHeader.replace('{header}', 'Identified Quivers');
      const allQuiverCategories = [QuiversCategory.ElementalQuivers, QuiversCategory.PhysicalQuivers, QuiversCategory.DoT];
      const selectedQuiverCategories = filter.showIdentifiedQuiverCategories.includes(QuiversCategory.All)
        ? allQuiverCategories
        : filter.showIdentifiedQuiverCategories.includes(QuiversCategory.None)
          ? []
          : filter.showIdentifiedQuiverCategories;

      for (const category of selectedQuiverCategories) {
        if (poe2 && ITEM_CATEGORIES_POE1_ONLY.includes(category as ModItemCategoryType)) {
          continue;
        }

        for (const config of enabledEquipmentConfigs) {
          const baseTypes = config.specificBaseTypes
            ? config.specificBaseTypes
            : (config.minGearTier
              ? this.helper.buildBaseTypesForTier(config.minGearTier, [WeaponType.Quivers])
              : ['All']);

          const modLine = this.buildModLine(category, config, filter);
          quiverRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
            this.getEquipmentType(config, 'gear'),
            new CustomRule({
              itemClass: 'Quivers',
              rarity: config.rarity,
              rarityComparator: config.rarityComparator,
              advancedConditions: new AdvancedConditions({
                corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                identified: YesNoAny.Yes,
                minItemLevel: config.minILevel
              }),
              baseTypes,
            }),
            filter,
            this.buildIdentifiedItemName(config, category),
            modLine
          ) + '\n\n';
        }
      }

      lifeArmourRules = filterHeader.replace('{header}', 'Identified Life Armour');
      const allLifeArmourCategories = [LifeArmourCategory.LifeBodyArmours, LifeArmourCategory.LifeShields, LifeArmourCategory.LifeHelmets, LifeArmourCategory.LifeGloves, LifeArmourCategory.LifeBoots];
      const selectedLifeArmourCategories = filter.showIdentifiedLifeArmourCategories.includes(LifeArmourCategory.All)
        ? allLifeArmourCategories
        : filter.showIdentifiedLifeArmourCategories.includes(LifeArmourCategory.None)
          ? []
          : filter.showIdentifiedLifeArmourCategories;

      for (const category of selectedLifeArmourCategories) {
        const armourTypes = (category === LifeArmourCategory.LifeBodyArmours)
          ? [ArmourType.BodyArmours]
          : (category === LifeArmourCategory.LifeShields)
            ? [ArmourType.Shields]
            : (category === LifeArmourCategory.LifeHelmets)
              ? [ArmourType.Helmets]
              : (category === LifeArmourCategory.LifeGloves)
                ? [ArmourType.Gloves]
                : (category === LifeArmourCategory.LifeBoots)
                  ? [ArmourType.Boots]
                  : [ArmourType.All];

        for (const config of enabledEquipmentConfigs) {
          const baseTypes = config.specificBaseTypes
            ? config.specificBaseTypes
            : (config.minGearTier
              ? this.helper.buildBaseTypesForTier(config.minGearTier, undefined, armourTypes, filter.showIdentifiedLifeArmourTypesDefenceTypes)
              : ['All']);

          const modLine = this.buildModLine(category, config, filter);
          lifeArmourRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
            this.getEquipmentType(config, 'gear'),
            new CustomRule({
              itemClass: armourTypes[0],
              rarity: config.rarity,
              rarityComparator: config.rarityComparator,
              advancedConditions: new AdvancedConditions({
                corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                identified: YesNoAny.Yes,
                minItemLevel: config.minILevel
              }),
              baseTypes,
            }),
            filter,
            this.buildIdentifiedItemName(config, category),
            modLine
          ) + '\n\n';
        }
      }

      esArmourRules = filterHeader.replace('{header}', 'Identified Energy Shield Armour');
      const allEsArmourCategories = [EnergyShieldArmourCategory.EsBodyArmours, EnergyShieldArmourCategory.EsShields, EnergyShieldArmourCategory.EsHelmets, EnergyShieldArmourCategory.EsGloves, EnergyShieldArmourCategory.EsBoots];
      const selectedEsArmourCategories = filter.showIdentifiedEnergyShieldArmourCategories.includes(EnergyShieldArmourCategory.All)
        ? allEsArmourCategories
        : filter.showIdentifiedEnergyShieldArmourCategories.includes(EnergyShieldArmourCategory.None)
          ? []
          : filter.showIdentifiedEnergyShieldArmourCategories;

      for (const category of selectedEsArmourCategories) {
        const armourTypes = (category === EnergyShieldArmourCategory.EsBodyArmours)
          ? [ArmourType.BodyArmours]
          : (category === EnergyShieldArmourCategory.EsShields)
            ? [ArmourType.Shields]
            : (category === EnergyShieldArmourCategory.EsHelmets)
              ? [ArmourType.Helmets]
              : (category === EnergyShieldArmourCategory.EsGloves)
                ? [ArmourType.Gloves]
                : (category === EnergyShieldArmourCategory.EsBoots)
                  ? [ArmourType.Boots]
                  : [ArmourType.All];

        for (const config of enabledEquipmentConfigs) {
          const baseTypes = config.specificBaseTypes
            ? config.specificBaseTypes
            : (config.minGearTier
              ? this.helper.buildBaseTypesForTier(config.minGearTier, undefined, armourTypes, filter.showIdentifiedEnergyShieldArmourTypesDefenceTypes)
              : ['All']);

          const modLine = this.buildModLine(category, config, filter);
          esArmourRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
            this.getEquipmentType(config, 'gear'),
            new CustomRule({
              itemClass: armourTypes[0],
              rarity: config.rarity,
              rarityComparator: config.rarityComparator,
              advancedConditions: new AdvancedConditions({
                corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                identified: YesNoAny.Yes,
                minItemLevel: config.minILevel
              }),
              baseTypes,
            }),
            filter,
            this.buildIdentifiedItemName(config, category),
            modLine
          ) + '\n\n';
        }
      }
    }

    if (filter.showIdentifiedJewellery) {
      jewelleryRules = filterHeader.replace('{header}', 'Identified Jewellery');
      const allJewelleryCategories = [
        JewelleryCategory.Belts, JewelleryCategory.MinionBelts, JewelleryCategory.CasterBelts,
        JewelleryCategory.AttackRings, JewelleryCategory.CasterRings, JewelleryCategory.MinionRings,
        JewelleryCategory.AttackAmulets, JewelleryCategory.CasterAmulets, JewelleryCategory.MinionAmulets
      ];
      const selectedJewelleryCategories: ModItemCategoryType[] = filter.showIdentifiedJewelleryCategories.includes(JewelleryCategory.All)
        ? allJewelleryCategories
        : filter.showIdentifiedJewelleryCategories.includes(JewelleryCategory.None)
          ? []
          : filter.showIdentifiedJewelleryCategories;

      for (const category of selectedJewelleryCategories) {
        if (poe2 && ITEM_CATEGORIES_POE1_ONLY.includes(category as ModItemCategoryType)) continue;
        if (poe1 && ITEM_CATEGORIES_POE2_ONLY.includes(category as ModItemCategoryType)) continue;

        const itemClass = category === JewelleryCategory.AttackAmulets || category === JewelleryCategory.CasterAmulets || category === JewelleryCategory.MinionAmulets
          ? 'Amulets'
          : category === JewelleryCategory.Belts || category === JewelleryCategory.MinionBelts || category === JewelleryCategory.CasterBelts
            ? 'Belts'
            : 'Rings';
        const baseTypes = category === JewelleryCategory.MinionRings && poe1 ? ['Bone Ring', 'Gloam Ring'] : ['All'];

        for (const config of enabledEquipmentConfigs) {
          const modLine = this.buildModLine(category, config, filter);
          jewelleryRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
            this.getEquipmentType(config, 'jewellery'),
            new CustomRule({
              rarity: config.rarity,
              rarityComparator: config.rarityComparator,
              advancedConditions: new AdvancedConditions({
                corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                identified: YesNoAny.Yes,
                minItemLevel: config.minILevel
              }),
              itemClass,
              baseTypes: config.specificBaseTypes || baseTypes,
            }),
            filter,
            this.buildIdentifiedItemName(config, category),
            modLine
          ) + '\n\n';
        }
      }
    }

    if (filter.showIdentifiedJewels) {
      jewelRules = filterHeader.replace('{header}', 'Identified Jewels');
      const allJewelCategories = poe1
        ? [JewelsCategory.MeleeAttackJewels, JewelsCategory.RangedAttackJewels, JewelsCategory.CasterJewels, JewelsCategory.MinionJewels, JewelsCategory.DotJewels]
        : [JewelsCategory.MeleeAttackJewels, JewelsCategory.RangedAttackJewels, JewelsCategory.CasterJewels, JewelsCategory.MinionJewels];
      const selectedJewelCategories: ModItemCategoryType[] = filter.showIdentifiedJewelCategories.includes(JewelsCategory.All)
        ? allJewelCategories
        : filter.showIdentifiedJewelCategories.includes(JewelsCategory.None)
          ? []
          : filter.showIdentifiedJewelCategories;

      for (const category of selectedJewelCategories) {
        const itemClass = 'Jewels'
        let baseTypes = ['All'];
        if (poe2) {
          baseTypes = category === JewelsCategory.MeleeAttackJewels
            ? ['Ruby']
            : category === JewelsCategory.RangedAttackJewels
              ? ['Emerald']
              : category === JewelsCategory.CasterJewels || category === JewelsCategory.MinionJewels
                ? ['Sapphire']
                : ['All']; // Just in case i'd forget to add a new category
        }

        for (const config of enabledJewelConfigs) {
            const modLine = this.buildModLine(category, config, filter, true);
            jewelRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
              this.getEquipmentType(config, 'jewels'),
              new CustomRule({
                rarity: config.rarity,
                rarityComparator: config.rarityComparator,
                advancedConditions: new AdvancedConditions({
                  corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                  identified: YesNoAny.Yes,
                  minItemLevel: config.minILevel
                }),
                itemClass,
                baseTypes: config.specificBaseTypes || baseTypes,
              }),
            filter,
            this.buildIdentifiedItemName(config, category),
            modLine
          ) + '\n\n';
        }
      }

      timeLostJewelRules = filterHeader.replace('{header}', 'Identified Time-Lost Jewels');
      if (poe2) {
        const allJewelCategories = [TimeLostJewelsCategory.CasterTimeLostJewels, TimeLostJewelsCategory.MeleeAttackTimeLostJewels, TimeLostJewelsCategory.RangedAttackTimeLostJewels];
        const selectedJewelCategories: TimeLostJewelsCategory[] = filter.showIdentifiedTimeLostJewelCategories.includes(TimeLostJewelsCategory.All)
          ? allJewelCategories
            : filter.showIdentifiedTimeLostJewelCategories.includes(TimeLostJewelsCategory.None)
            ? []
            : filter.showIdentifiedTimeLostJewelCategories;

        for (const category of selectedJewelCategories) {
          const itemClass = 'Jewels'
          const baseTypes = category === TimeLostJewelsCategory.CasterTimeLostJewels || category === TimeLostJewelsCategory.MinionTimeLostJewels
            ? ['Time-Lost Sapphire']
            : category === TimeLostJewelsCategory.MeleeAttackTimeLostJewels
              ? ['Time-Lost Ruby']
              : category === TimeLostJewelsCategory.RangedAttackTimeLostJewels
                ? ['Time-Lost Emerald']
                : ['All']; // Just in case i'd forget to add a new category

          for (const config of enabledJewelConfigs) {
            const modLine = this.buildModLine(category, config, filter, true);
            timeLostJewelRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
              this.getEquipmentType(config, 'jewels'),
              new CustomRule({
                rarity: config.rarity,
                rarityComparator: config.rarityComparator,
                advancedConditions: new AdvancedConditions({
                  corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                  identified: YesNoAny.Yes,
                  minItemLevel: config.minILevel
                }),
                itemClass,
                baseTypes: config.specificBaseTypes || baseTypes,
              }),
              filter,
              this.buildIdentifiedItemName(config, category),
              modLine
            ) + '\n\n';
          }
        }
      }

      if (poe1) {
        abyssJewelRules = filterHeader.replace('{header}', 'Identified Abyss Jewels');
        const allAbyssJewelCategories = [AbyssJewelsCategory.CasterAbyssJewels, AbyssJewelsCategory.MeleeAttackAbyssJewels, AbyssJewelsCategory.RangedAttackAbyssJewels, AbyssJewelsCategory.MinionAbyssJewels];
        const selectedAbyssJewelCategories: ModItemCategoryType[] = filter.showIdentifiedAbyssJewelCategories.includes(AbyssJewelsCategory.All)
          ? allAbyssJewelCategories
            : filter.showIdentifiedAbyssJewelCategories.includes(AbyssJewelsCategory.None)
            ? []
            : filter.showIdentifiedAbyssJewelCategories;

        for (const category of selectedAbyssJewelCategories) {
          const itemClass = 'Abyss Jewels'
          const baseTypes = ['All'];

          for (const config of enabledJewelConfigs) {
            const modLine = this.buildModLine(category, config, filter, true);
            abyssJewelRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
              this.getEquipmentType(config, 'jewels'),
              new CustomRule({
                rarity: config.rarity,
                rarityComparator: config.rarityComparator,
                advancedConditions: new AdvancedConditions({
                  corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                  identified: YesNoAny.Yes,
                  minItemLevel: config.minILevel
                }),
                itemClass,
                baseTypes: config.specificBaseTypes || baseTypes,
              }),
              filter,
              this.buildIdentifiedItemName(config, category),
              modLine
            ) + '\n\n';
          }
        }
      }
    }

    if (filter.showIdentifiedCustomItems) {
      customItemRules = filterHeader.replace('{header}', 'Identified Custom Items');

      for (const customModItemCategory of filter.customModItemCategories) {
        let customItemClass, baseTypes: string[] = [];
        let context: 'gear' | 'jewellery' | 'jewels' | 'custom' = 'custom';
        let enabledConfigs: IdentifiedItemConfig[] = [];

        if (customModItemCategory.itemType === CustomModItemType.Weapon) {
          customItemClass = this.helper.buildWeaponClass(customModItemCategory.weaponClasses);
          baseTypes = customModItemCategory.minimumGearTier === BaseTypeTier.Basic
            ? []
            : this.helper.buildBaseTypesForTier(customModItemCategory.minimumGearTier, customModItemCategory.weaponClasses);
          context = 'gear';
          enabledConfigs = enabledEquipmentConfigs;
        } else if (customModItemCategory.itemType === CustomModItemType.Armour) {
          customItemClass = this.helper.buildArmourClass(customModItemCategory.armourTypes);
          baseTypes = customModItemCategory.minimumGearTier === BaseTypeTier.Basic && customModItemCategory.defenceTypes.includes(DefenceType.All)
            ? []
            : this.helper.buildBaseTypesForTier(customModItemCategory.minimumGearTier, undefined, customModItemCategory.armourTypes, customModItemCategory.defenceTypes);
          context = 'gear';
          enabledConfigs = enabledEquipmentConfigs;
        } else if (customModItemCategory.itemType === CustomModItemType.Jewellery) {
          customItemClass = customModItemCategory.jewelleryClasses.includes(JewelleryType.All)
            ? '"Rings" "Amulets" "Belts"'
            : customModItemCategory.jewelleryClasses.map(c => `"${c}"`).join(' ');
          context = 'jewellery';
          enabledConfigs = enabledEquipmentConfigs;
        } else if (customModItemCategory.itemType === CustomModItemType.Jewel || customModItemCategory.itemType === CustomModItemType.AbyssJewel || customModItemCategory.itemType === CustomModItemType.TimeLostJewel) {
          if (customModItemCategory.itemType === CustomModItemType.AbyssJewel) {
            customItemClass = '"Abyss Jewels"';
          } else {
            customItemClass = '"Jewels"';
          }
          baseTypes = customModItemCategory.jewelBaseTypes;
          context = 'jewels';
          enabledConfigs = enabledJewelConfigs;
        }

        const isJewel = customModItemCategory.itemType === CustomModItemType.Jewel || customModItemCategory.itemType === CustomModItemType.AbyssJewel || customModItemCategory.itemType === CustomModItemType.TimeLostJewel;

        let customConfig = false;
        if (customModItemCategory.customConfiguration && customModItemCategory.customConfiguration.enabled) {
          enabledConfigs = [customModItemCategory.customConfiguration];
          customConfig = true;
        }

        for (const config of enabledConfigs) {
          const modLine = this.buildModLine(customModItemCategory.name, config, filter, isJewel);
          modLine.customClass = customItemClass;

          customItemRules += !modLine.customLine ? '' : this.ruleFormatter.generateEquipmentRule(
            this.getEquipmentType(config, context),
            new CustomRule({
              rarity: config.rarity,
              rarityComparator: config.rarityComparator,
              advancedConditions: new AdvancedConditions({
                corrupted: config.allowCorrupted ? YesNoAny.Any : YesNoAny.No,
                identified: YesNoAny.Yes,
                minItemLevel: config.minILevel
              }),
              baseTypes: config.specificBaseTypes || baseTypes,
            }),
            filter,
            `Identified ${config.name} Custom ${customModItemCategory.name}`,
            modLine
          ) + '\n\n';
        }
      }
    }

    IdentifiedItemGeneratorService.cache[cacheKey] = customItemRules + '\n\n' + weaponRules + '\n\n' + casterWeaponRules + '\n\n' + gemWeaponRules + '\n\n' + quiverRules + '\n\n'
      + lifeArmourRules + '\n\n' + esArmourRules + '\n\n' + jewelleryRules + '\n\n' + jewelRules + '\n\n' + abyssJewelRules + '\n\n' + timeLostJewelRules;

    return IdentifiedItemGeneratorService.cache[cacheKey];
  }

  public generateRemainingIdentifiedGearRules(filter: Filter): string {
    if (!filter.hideRemainingIdentifiedItems) {
      return '';
    }

    const classes = this.itemDataService.getAllGearTypes().map(g => `"${g}"`).join(' ');
    return this.ruleFormatter.generateRule(new CustomRule({
      advancedConditions: new AdvancedConditions({ identified: YesNoAny.Yes, corrupted: filter.hideRemainingIdentifiedItemsIncludingCorrupted ? YesNoAny.Any : YesNoAny.No }),
      rarityComparator: Comparator.LessThanOrEqual,
      rarity: Rarity.Rare,
      filterItemClass: classes,
      displayOptions: new DisplayOptions({
        hidden: true,
      }),
    }), filter, 'equipment-hide-remaining-identified', 'Remaining Identified Gear') + '\n\n';
  }

  public generateRemainingIdentifiedJewelleryRules(filter: Filter): string {
    if (!filter.hideRemainingIdentifiedItems) {
      return '';
    }

    const classes = this.itemDataService.getAllJewelClasses() + ' ' + this.itemDataService.getAllJewelleryClassesWithoutTalismans();
    return this.ruleFormatter.generateRule(new CustomRule({
      advancedConditions: new AdvancedConditions({ identified: YesNoAny.Yes, corrupted: filter.hideRemainingIdentifiedItemsIncludingCorrupted ? YesNoAny.Any : YesNoAny.No }),
      rarityComparator: Comparator.LessThanOrEqual,
      rarity: Rarity.Rare,
      filterItemClass: classes,
      displayOptions: new DisplayOptions({
        hidden: true,
      }),
    }), filter, 'equipment-hide-remaining-identified', 'Remaining Identified Jewellery') + '\n\n';
  }

  private buildIdentifiedItemName(config: IdentifiedItemConfig, category: ModItemCategoryType | string, itemType?: string): string {
    if (config.name) {
      return `Identified ${config.name.replace(' Equipment', '').replace(' Jewels', '')} ${category}${itemType ? ` ${itemType}` : ''}`;
    }

    // Use the same auto-generated name format as shown in the placeholder
    const tier = config.itemTier === ItemTier.Excellent ? 'Excellent' : config.itemTier === ItemTier.Good ? 'Good' : '';
    const rarity = config.rarity + (config.rarityComparator === Comparator.GreaterThanOrEqual ? '+' : '');
    const type = config.type === IdentifiedItemConfigType.Equipment ? 'Equipment' : config.type === IdentifiedItemConfigType.Jewels ? 'Jewels' : '';
    const autoGeneratedName = `Custom ${tier} ${rarity} ${type}`.trim();

    return `Identified ${autoGeneratedName} ${category}${itemType ? ` ${itemType}` : ''}`;
  }

  generateRestrictedModIdentifiedItemsRules(filter: Filter): string {
    if (!filter.showRestrictedModIdentifiedItems) return '';
    if (!filter.showRestrictedModIdentifiedItemsDelve && !filter.showRestrictedModIdentifiedItemsIncursion && !filter.showRestrictedModIdentifiedItemsBreach) return '';

    const restrictedModIdentifiedWeaponClasses = filter.showRestrictedModIdentifiedItemsWeaponTypes.includes(WeaponType.All) ? this.itemDataService.getAttackWeaponTypes() : filter.showRestrictedModIdentifiedItemsWeaponTypes;
    const restrictedModIdentifiedWeaponFilterItemClass = restrictedModIdentifiedWeaponClasses.map(w => `"${w}"`).join(' ');
    const restrictedModIdentifiedWeaponBaseTypes = filter.showRestrictedModIdentifiedItemsGearTier === BaseTypeTier.Basic
      && filter.showRestrictedModIdentifiedItemsWeaponTypes.includes(WeaponType.All) ? ['All']
      : this.helper.buildBaseTypesForTier(filter.showRestrictedModIdentifiedItemsGearTier, restrictedModIdentifiedWeaponClasses);

    let customLine = `\n  HasExplicitMod >=1`;

    if (filter.showRestrictedModIdentifiedItemsDelve) {
      customLine += ` "Subterranean" "of the Underground" "of Craiceann" "of Farrul" "of Saqawal" "of Fenumus"`;
    }
    if (filter.showRestrictedModIdentifiedItemsIncursion) {
      customLine += ` "Citaqualotl's" "Guatelitzi's" "Matatl's" "Tacati's" "Topotante's" "Xopec's" "of Citaqualotl" "of Guatelitzi" "of Matatl" "of Puhuarte" "of Tacati"`;
    }
    if (filter.showRestrictedModIdentifiedItemsBreach) {
      customLine += ` "Chayula's" "Esh's" "Tul's" "Uul-Netol's" "Xoph's" "of Chayula" "of Esh" "of Tul" "of Uul-Netol" "of Xoph"`;
    }

    customLine = this.normalizeModLine(customLine);

    const restrictedModIdentifiedWeaponsRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherIdentifiedRestrictedModItems, new CustomRule({
      advancedConditions: new AdvancedConditions({ identified: YesNoAny.Yes }),
      filterItemClass: restrictedModIdentifiedWeaponFilterItemClass,
      baseTypes: restrictedModIdentifiedWeaponBaseTypes,
    }), filter, 'Identified Restricted Mod Weapons', { customLine }) + '\n\n';

    const restrictedModIdentifiedArmourClasses = this.itemDataService.getAllArmourTypes().map(w => `"${w}"`).join(' ');
    const restrictedModIdentifiedArmourBaseTypes = filter.showRestrictedModIdentifiedItemsGearTier === BaseTypeTier.Basic
      && filter.showRestrictedModIdentifiedItemsDefenceTypes.includes(DefenceType.All) ? ['All']
      : this.helper.buildBaseTypesForTier(filter.showRestrictedModIdentifiedItemsGearTier, undefined, [ArmourType.All], filter.showRestrictedModIdentifiedItemsDefenceTypes);

    const restrictedModIdentifiedDefenceTypesRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherIdentifiedRestrictedModItems, new CustomRule({
      advancedConditions: new AdvancedConditions({ identified: YesNoAny.Yes }),
      filterItemClass: restrictedModIdentifiedArmourClasses,
      baseTypes: restrictedModIdentifiedArmourBaseTypes,
    }), filter, 'Identified Restricted Mod Armour', { customLine }) + '\n\n';

    const restrictedModIdentifiedJewelleryClasses = '"Rings" "Amulets" "Belts"';

    const restrictedModIdentifiedJewelleryRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherIdentifiedRestrictedModItems, new CustomRule({
      advancedConditions: new AdvancedConditions({ identified: YesNoAny.Yes }),
      filterItemClass: restrictedModIdentifiedJewelleryClasses,
    }), filter, 'Identified Restricted Mod Jewellery', { customLine }) + '\n\n';

    return restrictedModIdentifiedWeaponsRule + restrictedModIdentifiedDefenceTypesRule + restrictedModIdentifiedJewelleryRule;
  }

  private getEquipmentType(config: IdentifiedItemConfig, context: 'gear' | 'jewellery' | 'jewels' | 'custom'): EquipmentType {
    const tier = config.itemTier === ItemTier.Excellent ? 'Excellent' : 'Good';

    if (context === 'gear') {
      return tier === 'Excellent' ? EquipmentType.OtherExcellentIdentifiedGear : EquipmentType.OtherGoodIdentifiedGear;
    } else if (context === 'jewellery') {
      return tier === 'Excellent' ? EquipmentType.OtherExcellentIdentifiedJewellery : EquipmentType.OtherGoodIdentifiedJewellery;
    } else if (context === 'jewels') {
      return tier === 'Excellent' ? EquipmentType.OtherExcellentIdentifiedJewels : EquipmentType.OtherGoodIdentifiedJewels;
    } else { // custom
      return tier === 'Excellent' ? EquipmentType.OtherExcellentIdentifiedCustomItems : EquipmentType.OtherGoodIdentifiedCustomItems;
    }
  }

  private normalizeModLine(line: string): string {
    const hasExplicitModIndex = line.indexOf('HasExplicitMod');
    if (hasExplicitModIndex !== -1) {
      // Preserve everything before and including "HasExplicitMod", normalize spaces after
      const beforeHasExplicitMod = line.substring(0, hasExplicitModIndex + 'HasExplicitMod'.length);
      const afterHasExplicitMod = line.substring(hasExplicitModIndex + 'HasExplicitMod'.length);
      // Replace 2+ spaces with a single space in the part after HasExplicitMod
      const normalizedAfter = afterHasExplicitMod.replace(/  +/g, ' ');
      return beforeHasExplicitMod + normalizedAfter;
    }
    return line;
  }

  private buildSingleModLine(modItemCategory: ModItemCategoryType | string, config: IdentifiedItemConfig, filter: Filter): Partial<FormatRuleOptions> | undefined {
    const modTierData = this.modTierService.getModTierData();
    const tierData = modTierData.filter(t => t.cat === modItemCategory);

    // Determine which tier to use based on config
    let targetTier: string = 'Good';
    if (config.minPerfectMods || config.minPerfectPrefixes || config.minPerfectSuffixes) {
      targetTier = 'Perfect';
    } else if (config.minGreatMods || config.minGreatPrefixes || config.minGreatSuffixes || config.itemTier === ItemTier.Excellent) {
      targetTier = 'Great';
    }

    const mods = tierData.filter(m => m.tier === targetTier);
    const badMods = tierData.filter(m => m.tier === 'Bad');

    if (mods.length === 0) return undefined;

    const modsString = mods.map(m => `"${m.name}"`).join(' ');
    const badModsString = badMods.map(m => `"${m.name}"`).join(' ');

    let customLine = this.normalizeModLine(`\n  HasExplicitMod >=1 ${modsString}`);
    if (badModsString && config.maxBadMods !== undefined && config.maxBadMods === 0) {
      customLine += this.normalizeModLine(`\n  HasExplicitMod =0 ${badModsString}`);
    }

    return {
      customLine,
      overrideCosmetics: filter.identifiedItemsStyleOverride?.displayOptions.active ? filter.identifiedItemsStyleOverride?.displayOptions?.cosmeticOptions : undefined,
    };
  }

  private buildModLine(modItemCategory: ModItemCategoryType | string, config: IdentifiedItemConfig, filter: Filter, isJewel: boolean = false): Partial<FormatRuleOptions> {
    const modTierData = this.modTierService.getModTierData();
    const tierData = modTierData.filter(t => t.cat === modItemCategory);
    const prefixes = tierData.filter(m => m.affixType === 'Prefix');
    const suffixes = tierData.filter(m => m.affixType === 'Suffix');
    const perfectPrefixes = [...new Set(prefixes.filter(p => p.tier === "Perfect").map(p => p.name))];
    const greatPrefixes = [...new Set(prefixes.filter(p => p.tier === "Great").map(p => p.name))];
    const goodPrefixes = [...new Set(prefixes.filter(p => p.tier === "Good").map(p => p.name))];
    const badPrefixes = [...new Set(prefixes.filter(p => p.tier === "Bad").map(p => p.name))];
    const perfectSuffixes = [...new Set(suffixes.filter(s => s.tier === "Perfect").map(s => s.name))];
    const greatSuffixes = [...new Set(suffixes.filter(s => s.tier === "Great").map(s => s.name))];
    const goodSuffixes = [...new Set(suffixes.filter(s => s.tier === "Good").map(s => s.name))];
    const badSuffixes = [...new Set(suffixes.filter(s => s.tier === "Bad").map(s => s.name))];

    const perfectPrefixesString = perfectPrefixes?.map(p => `"${p}"`).join(' ');
    const greatPrefixesString = greatPrefixes?.map(p => `"${p}"`).join(' ');
    const goodPrefixesString = goodPrefixes?.map(p => `"${p}"`).join(' ');
    const badPrefixesString = badPrefixes?.map(p => `"${p}"`).join(' ');
    const perfectSuffixesString = perfectSuffixes?.map(s => `"${s}"`).join(' ');
    const greatSuffixesString = greatSuffixes?.map(s => `"${s}"`).join(' ');
    const goodSuffixesString = goodSuffixes?.map(s => `"${s}"`).join(' ');
    const badSuffixesString = badSuffixes?.map(s => `"${s}"`).join(' ');

    let customLine = '';

    // Min Perfect Prefixes check
    if (config.minPerfectPrefixes && perfectPrefixesString) {
      const availableMods = perfectPrefixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minPerfectPrefixes ?? 0);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectPrefixesString}`);
    }

    // Min Perfect Suffixes check
    if (config.minPerfectSuffixes && perfectSuffixesString) {
      const availableMods = perfectSuffixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minPerfectSuffixes ?? 0);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectSuffixesString}`);
    }

    // Min Perfect Mods check (combined prefixes + suffixes)
    if (config.minPerfectMods && (perfectPrefixesString || perfectSuffixesString)) {
      const availableMods = perfectPrefixes.length + perfectSuffixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minPerfectMods ?? 0);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectPrefixesString} ${perfectSuffixesString}`);
    }

    // Min Great Prefixes check (combined perfect + great prefixes)
    if (config.minGreatPrefixes && (perfectPrefixesString || greatPrefixesString)) {
      const availableMods = perfectPrefixes.length + greatPrefixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minGreatPrefixes ?? 0);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectPrefixesString} ${greatPrefixesString}`);
    }

    // Min Great Suffixes check (combined perfect + great suffixes)
    if (config.minGreatSuffixes && (perfectSuffixesString || greatSuffixesString)) {
      const availableMods = perfectSuffixes.length + greatSuffixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minGreatSuffixes ?? 0);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectSuffixesString} ${greatSuffixesString}`);
    }

    // Min Great Mods check (combined perfect + great prefixes + suffixes)
    if (config.minGreatMods && (perfectPrefixesString || perfectSuffixesString || greatPrefixesString || greatSuffixesString)) {
      const availableMods = perfectPrefixes.length + perfectSuffixes.length + greatPrefixes.length + greatSuffixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minGreatMods ?? 0);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectPrefixesString} ${perfectSuffixesString} ${greatPrefixesString} ${greatSuffixesString}`);
    }

    // Min Good Mods check (combined perfect + great + good mods)
    if (config.minGoodMods && (perfectPrefixesString || perfectSuffixesString || greatPrefixesString || greatSuffixesString || goodPrefixesString || goodSuffixesString)) {
      const availableMods = perfectPrefixes.length + perfectSuffixes.length + greatPrefixes.length + goodPrefixes.length + greatSuffixes.length + goodSuffixes.length;
      const effectiveMinMods = Math.min(availableMods, config.minGoodMods);
      customLine += this.normalizeModLine(`\n  HasExplicitMod >=${effectiveMinMods} ${perfectPrefixesString} ${perfectSuffixesString} ${greatPrefixesString} ${greatSuffixesString} ${goodPrefixesString} ${goodSuffixesString}`);
    }

    // Max Bad Mods check
    if (config.maxBadMods !== null && config.maxBadMods !== undefined && (badPrefixesString || badSuffixesString)) {
      if (config.maxBadMods === 0) {
        customLine += this.normalizeModLine(`\n  HasExplicitMod =0 ${badPrefixesString} ${badSuffixesString}`);
      } else {
        customLine += this.normalizeModLine(`\n  HasExplicitMod <=${config.maxBadMods} ${badPrefixesString} ${badSuffixesString}`);
      }
    }

    return {
      customLine,
      overrideCosmetics: filter.identifiedItemsStyleOverride?.displayOptions.active ? filter.identifiedItemsStyleOverride?.displayOptions?.cosmeticOptions : undefined,
    };
  }
}
